import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  FileText, 
  CheckCircle, 
  Shield, 
  Clock, 
  Car,
  Upload,
  CreditCard,
  ArrowRight,
  Star,
  AlertTriangle,
  Phone,
  MessageCircle,
  HelpCircle,
  ExternalLink,
  Menu,
  X,
  User
} from "lucide-react";
import CallbackRequestModal from "../components/CallbackRequestModal";

const LandingPage = () => {
  const navigate = useNavigate();
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const steps = [
    {
      icon: FileText,
      title: "Remplissez le formulaire",
      description: "Entrez vos coordonnées et les informations du véhicule"
    },
    {
      icon: Upload,
      title: "Scannez vos documents",
      description: "Utilisez votre téléphone pour scanner tous les justificatifs"
    },
    {
      icon: CreditCard,
      title: "Réglez en ligne",
      description: "Paiement sécurisé par carte bancaire"
    },
    {
      icon: CheckCircle,
      title: "Recevez votre carte grise",
      description: "Votre dossier est traité par nos experts"
    }
  ];

  const features = [
    {
      icon: Shield,
      title: "Agrée Préfecture",
      description: "Habilitation officielle du Ministère de l'Intérieur"
    },
    {
      icon: Clock,
      title: "Traitement rapide",
      description: "Dossier traité sous 24 à 48h ouvrées"
    },
    {
      icon: CheckCircle,
      title: "100% en ligne",
      description: "Aucun déplacement nécessaire"
    }
  ];

  const reviews = [
    {
      name: "Pierre M.",
      location: "Lyon",
      rating: 5,
      text: "Service impeccable ! J'ai importé ma BMW d'Allemagne et tout a été géré en 48h. Les CERFA étaient pré-remplis, il ne me restait plus qu'à signer.",
      vehicle: "BMW Série 3 - Allemagne"
    },
    {
      name: "Sophie L.",
      location: "Bordeaux", 
      rating: 5,
      text: "Très professionnel. L'import de documents sur téléphone est vraiment pratique. Mon dossier était complet du premier coup.",
      vehicle: "Audi A4 - Belgique"
    },
    {
      name: "Marc D.",
      location: "Marseille",
      rating: 5,
      text: "Excellente expérience. J'avais des doutes sur l'import de mon véhicule des Pays-Bas, leur équipe m'a accompagné de A à Z.",
      vehicle: "Volkswagen Golf - Pays-Bas"
    },
    {
      name: "Julie R.",
      location: "Paris",
      rating: 4,
      text: "Service rapide et efficace. Le prix est correct vu le temps gagné. Je recommande pour tous ceux qui importent un véhicule.",
      vehicle: "Mercedes Classe C - Allemagne"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#000091] rounded-lg flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-[#000091] text-lg leading-tight">Carte Grise Étrangère</span>
                <p className="text-xs text-slate-500">Service agréé</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6" aria-label="Navigation principale">
              <a href="#process" className="text-slate-600 hover:text-[#000091] text-sm font-medium">Comment ça marche</a>
              <a href="#pricing" className="text-slate-600 hover:text-[#000091] text-sm font-medium">Tarifs</a>
              <a href="#reviews" className="text-slate-600 hover:text-[#000091] text-sm font-medium">Avis clients</a>
              <Button 
                onClick={() => navigate('/connexion')}
                variant="outline"
                className="border-[#000091] text-[#000091]"
                data-testid="client-login-btn"
              >
                Mon espace
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-[#000091]" />
              ) : (
                <Menu className="w-6 h-6 text-[#000091]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 shadow-lg" data-testid="mobile-menu">
            <nav className="px-4 py-4 space-y-3">
              <a 
                href="#process" 
                className="block py-2 px-3 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-[#000091] font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Comment ça marche
              </a>
              <a 
                href="#pricing" 
                className="block py-2 px-3 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-[#000091] font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Tarifs
              </a>
              <a 
                href="#reviews" 
                className="block py-2 px-3 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-[#000091] font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Avis clients
              </a>
              <div className="pt-2 border-t border-slate-200">
                <Button 
                  onClick={() => {
                    navigate('/connexion');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full bg-[#000091] hover:bg-[#000091]/90"
                  data-testid="mobile-login-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Mon espace
                </Button>
              </div>
              <Button 
                onClick={() => {
                  navigate('/demande');
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-[#ED2939] hover:bg-[#ED2939]/90"
              >
                Commencer ma demande
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </nav>
          </div>
        )}

        {/* Tricolor bar */}
        <div className="h-1 flex">
          <div className="flex-1 bg-[#002395]"></div>
          <div className="flex-1 bg-white"></div>
          <div className="flex-1 bg-[#ED2939]"></div>
        </div>
      </header>

      {/* Hero Section with Registration Cards */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Background with real registration card images - Desktop only */}
        <div className="absolute inset-0 z-0 overflow-hidden hidden lg:block">
          {/* German Fahrzeugbrief */}
          <div className="absolute top-16 right-[15%] w-72 h-auto transform rotate-6 opacity-80 shadow-2xl rounded-lg overflow-hidden border-2 border-white">
            <img 
              src="https://customer-assets.emergentagent.com/job_foreign-cartegrise/artifacts/romjgu05_carte-grise-allemande-Fahrzeugbrief.jpg" 
              alt="Carte grise allemande (Fahrzeugbrief)"
              className="w-full h-auto"
            />
          </div>
          
          {/* French Carte Grise */}
          <div className="absolute top-36 right-[22%] w-72 h-auto transform -rotate-3 opacity-95 shadow-2xl rounded-lg overflow-hidden border-2 border-white">
            <img 
              src="https://customer-assets.emergentagent.com/job_foreign-cartegrise/artifacts/5tnwwcwr_cg%20fr.jpg" 
              alt="Carte grise française (Certificat d'immatriculation)"
              className="w-full h-auto"
            />
          </div>
          
          {/* Arrow transformation indicator */}
          <div className="absolute top-32 right-[12%] flex items-center justify-center w-14 h-14 bg-[#000091] rounded-full shadow-lg z-10 border-4 border-white">
            <ArrowRight className="w-7 h-7 text-white" />
          </div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              Service habilité par le Ministère de l'Intérieur
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] tracking-tight mb-6">
              Immatriculez votre véhicule <span className="text-[#000091]">importé</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
              Service en ligne pour l'immatriculation de véhicules étrangers en France. 
              Génération automatique des CERFA, signature électronique et téléchargement de documents simplifié.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => navigate('/demande')}
                className="bg-[#000091] hover:bg-[#000091]/90 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                data-testid="start-registration-btn"
              >
                Commencer ma demande
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline"
                className="border-[#000091] text-[#000091] hover:bg-[#000091]/5 px-8 py-6 text-lg"
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                data-testid="view-pricing-btn"
              >
                Voir les tarifs
              </Button>
            </div>
          </div>
          
          {/* Mobile: Registration cards below text */}
          <div className="lg:hidden mt-10 flex justify-center items-center">
            <div className="relative w-full max-w-sm">
              {/* German card - behind */}
              <div className="absolute top-0 left-0 w-40 h-auto transform -rotate-6 shadow-xl rounded-lg overflow-hidden border-2 border-white z-10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_foreign-cartegrise/artifacts/romjgu05_carte-grise-allemande-Fahrzeugbrief.jpg" 
                  alt="Carte grise allemande"
                  className="w-full h-auto"
                />
              </div>
              
              {/* Arrow */}
              <div className="absolute top-12 left-1/2 -translate-x-1/2 flex items-center justify-center w-10 h-10 bg-[#000091] rounded-full shadow-lg z-30 border-2 border-white">
                <ArrowRight className="w-5 h-5 text-white" />
              </div>
              
              {/* French card - in front */}
              <div className="absolute top-4 right-0 w-40 h-auto transform rotate-3 shadow-xl rounded-lg overflow-hidden border-2 border-white z-20">
                <img 
                  src="https://customer-assets.emergentagent.com/job_foreign-cartegrise/artifacts/5tnwwcwr_cg%20fr.jpg" 
                  alt="Carte grise française"
                  className="w-full h-auto"
                />
              </div>
              
              {/* Spacer for height */}
              <div className="h-48"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notice - EU vs Non-EU */}
      <section className="py-6 bg-[#000091]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-white">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                  <text x="12" y="16" textAnchor="middle" fontSize="10" fill="currentColor">EU</text>
                </svg>
              </div>
              <p className="text-sm md:text-base">
                <strong>Véhicules Union Européenne :</strong> Notre service en ligne traite tous les véhicules importés de l'UE
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-[#ED2939] text-white px-4 py-2 rounded-lg">
                <Phone className="w-4 h-4" />
                <span className="text-sm font-medium">Hors UE ? Contactez un expert</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-4 p-4">
                <div className="w-12 h-12 rounded-full bg-[#000091]/10 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-[#000091]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A]">{feature.title}</h3>
                  <p className="text-sm text-slate-500">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section id="process" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">Comment ça marche ?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Une procédure simplifiée en 4 étapes pour obtenir votre carte grise française
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <Card key={index} className="bg-white border-slate-200 hover:border-[#000091]/30 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-[#000091] text-white flex items-center justify-center mb-4">
                    <step.icon className="w-6 h-6" />
                  </div>
                  <div className="text-sm font-medium text-[#000091] mb-2">Étape {index + 1}</div>
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{step.title}</h3>
                  <p className="text-slate-500 text-sm">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">Nos tarifs</h2>
            <p className="text-lg text-slate-600">Frais de dossier TTC</p>
          </div>

          {/* Important pricing info */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-semibold mb-1">Frais supplémentaires obligatoires</p>
                  <p className="text-amber-700">
                    En plus de nos frais de dossier, s'ajouteront les taxes officielles : 
                    <strong> taxe régionale</strong> (variable selon la région et la puissance fiscale), 
                    <strong> taxe de gestion</strong> (~11€), et le cas échéant le <strong>malus écologique</strong>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery time notice */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Délais de traitement</p>
                  <p className="text-blue-700">
                    Selon votre région de résidence, le délai de traitement des dossiers définitifs par les services de l'État 
                    <strong> varie de 1 à 5 semaines</strong>. Notre équipe traite votre dossier sous 24-48h après réception complète.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Standard */}
            <Card className="bg-white border-2 border-slate-200 hover:border-[#000091] transition-all">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-2">Immatriculation Définitive</h3>
                <p className="text-slate-500 mb-6">Pour les véhicules déjà dédouanés</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-[#000091]">99€</span>
                  <span className="text-slate-500 ml-2">TTC</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Génération des CERFA
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Signature électronique
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Scanner de documents
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Traitement sous 48h
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/demande?package=standard')}
                  className="w-full bg-[#000091] hover:bg-[#000091]/90 text-white py-6"
                  data-testid="select-standard-btn"
                >
                  Choisir cette formule
                </Button>
              </CardContent>
            </Card>

            {/* Premium */}
            <Card className="bg-[#000091] border-2 border-[#000091] relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-[#ED2939] text-white text-xs font-bold px-3 py-1 rounded-full">
                POPULAIRE
              </div>
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold text-white mb-2">W Provisoire + Définitive</h3>
                <p className="text-white/70 mb-6">Immatriculation provisoire puis définitive</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-white">149€</span>
                  <span className="text-white/70 ml-2">TTC</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-white">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Immatriculation W provisoire
                  </li>
                  <li className="flex items-center gap-2 text-white">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Immatriculation définitive
                  </li>
                  <li className="flex items-center gap-2 text-white">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Tous les services inclus
                  </li>
                  <li className="flex items-center gap-2 text-white">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Support prioritaire
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/demande?package=premium')}
                  className="w-full bg-white text-[#000091] hover:bg-slate-100 py-6"
                  data-testid="select-premium-btn"
                >
                  Choisir cette formule
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section id="reviews" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">Avis de nos clients</h2>
            <p className="text-lg text-slate-600">Ils ont fait confiance à notre service</p>
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="flex">
                {[1,2,3,4,5].map((star) => (
                  <Star key={star} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <span className="text-lg font-semibold text-[#0F172A]">4.8/5</span>
              <span className="text-slate-500">basé sur 127 avis</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviews.map((review, index) => (
              <Card key={index} className="bg-white border-slate-200 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                    {[...Array(5 - review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-slate-200" />
                    ))}
                  </div>
                  <p className="text-slate-600 text-sm mb-4 leading-relaxed">"{review.text}"</p>
                  <div className="border-t border-slate-100 pt-4">
                    <p className="font-semibold text-[#0F172A]">{review.name}</p>
                    <p className="text-xs text-slate-500">{review.location}</p>
                    <p className="text-xs text-[#000091] mt-1">{review.vehicle}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Required */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">Documents requis</h2>
            <p className="text-lg text-slate-600">Préparez ces documents pour votre demande</p>
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-lg mt-4 text-sm">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <text x="12" y="16" textAnchor="middle" fontSize="8" fill="currentColor">EU</text>
              </svg>
              Véhicules importés de l'Union Européenne uniquement
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {[
              { name: "Cession ou facture d'achat", required: true },
              { name: "Carte(s) grise(s) étrangère(s)", required: true, hint: "Teil I et Teil II pour l'Allemagne" },
              { name: "Quitus fiscal", required: true, hasGuide: true },
              { name: "Contrôle technique (-6 mois)", required: false, hint: "Si véhicule de plus de 4 ans" },
              { name: "Attestation d'assurance", required: true },
              { name: "Justificatif de domicile (-6 mois)", required: true, hint: "Facture électricité, téléphone..." },
              { name: "Permis de conduire", required: true },
              { name: "Pièce d'identité", required: true },
              { name: "Certificat de conformité européen (COC)", required: false, hint: "Si TVV ou n° réception incomplet", special: true, hasCocPartner: true }
            ].map((doc, index) => (
              <div key={index} className={`flex items-start gap-3 p-4 rounded-lg ${doc.special ? 'bg-amber-50 border border-amber-200' : 'bg-white border border-slate-200'}`}>
                {doc.special ? (
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <FileText className={`w-5 h-5 flex-shrink-0 mt-0.5 ${doc.required ? 'text-[#000091]' : 'text-slate-400'}`} />
                )}
                <div className="flex-1">
                  <span className={`text-sm font-medium ${doc.special ? 'text-amber-800' : 'text-slate-700'}`}>
                    {doc.name}
                    {doc.required && <span className="text-red-500 ml-1">*</span>}
                  </span>
                  {doc.hint && (
                    <p className={`text-xs mt-0.5 ${doc.special ? 'text-amber-600' : 'text-slate-500'}`}>{doc.hint}</p>
                  )}
                  {doc.hasGuide && (
                    <Link 
                      to="/guide/quitus-fiscal" 
                      className="inline-flex items-center gap-1 text-xs text-[#000091] hover:underline mt-1"
                    >
                      <HelpCircle className="w-3 h-3" />
                      Comment l'obtenir ?
                    </Link>
                  )}
                  {doc.hasCocPartner && (
                    <a 
                      href="#coc-partner" 
                      className="inline-flex items-center gap-1 text-xs text-amber-700 hover:underline mt-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Obtenir un COC
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* COC Partner Section */}
          <div id="coc-partner" className="mt-12 max-w-3xl mx-auto">
            <Card className="border-[#000091]/30 bg-[#000091]/5">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#000091] rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-2">Besoin d'un Certificat de Conformité (COC) ?</h3>
                    <p className="text-slate-600 text-sm mb-4">
                      Si les informations de votre carte grise étrangère ne permettent pas d'obtenir une immatriculation définitive 
                      (TVV incomplet, numéro de réception européen manquant), vous aurez besoin d'un Certificat de Conformité Européen.
                      <br/><br/>
                      Grâce à notre partenariat avec un fournisseur agréé, obtenez votre COC rapidement et à tarif préférentiel.
                    </p>
                    <Button 
                      variant="outline" 
                      className="border-[#000091] text-[#000091] hover:bg-[#000091]/5"
                      onClick={() => window.open('https://www.eurococ.eu/fr', '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Commander un COC partenaire
                    </Button>
                    <p className="text-xs text-slate-500 mt-2">
                      Service partenaire externe - Délai moyen : 5 à 15 jours ouvrés
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Non-EU Notice */}
          <div className="mt-8 max-w-3xl mx-auto">
            <Card className="border-[#ED2939]/30 bg-red-50/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#ED2939]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#ED2939]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-2">Véhicule importé hors Union Européenne ?</h3>
                    <p className="text-slate-600 text-sm mb-4">
                      Les véhicules importés de pays hors UE (USA, Royaume-Uni, Suisse, etc.) nécessitent 
                      des démarches supplémentaires : dédouanement, homologation DREAL, certificat de conformité...
                    </p>
                    <Button 
                      variant="outline" 
                      className="border-[#ED2939] text-[#ED2939] hover:bg-[#ED2939]/5"
                      onClick={() => setShowCallbackModal(true)}
                      data-testid="contact-expert-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Contacter un expert
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#000091]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Prêt à immatriculer votre véhicule ?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Commencez votre demande en ligne en quelques minutes
          </p>
          <Button 
            onClick={() => navigate('/demande')}
            className="bg-white text-[#000091] hover:bg-slate-100 px-8 py-6 text-lg font-semibold"
            data-testid="cta-start-btn"
          >
            Commencer maintenant
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Logo */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <Car className="w-6 h-6 text-[#000091]" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Carte Grise Étrangère</h3>
                  <p className="text-slate-400 text-sm">Service agréé</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm">
                Service habilité par le Ministère de l'Intérieur pour les démarches d'immatriculation.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold mb-4">Informations</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <Link to="/guide/quitus-fiscal" className="hover:text-white transition-colors">
                    Guide Quitus Fiscal
                  </Link>
                </li>
                <li>
                  <a href="#coc-partner" className="hover:text-white transition-colors">
                    Obtenir un COC
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="hover:text-white transition-colors">
                    Nos tarifs
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">Légal</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <Link to="/cgv" className="hover:text-white transition-colors">
                    Conditions Générales de Vente
                  </Link>
                </li>
                <li>
                  <Link to="/mentions-legales" className="hover:text-white transition-colors">
                    Mentions légales
                  </Link>
                </li>
                <li>
                  <Link to="/confidentialite" className="hover:text-white transition-colors">
                    Politique de confidentialité
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-400">
              © 2024 Carte Grise Étrangère. Tous droits réservés.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Shield className="w-4 h-4" />
                Paiement sécurisé
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal de demande de rappel */}
      <CallbackRequestModal 
        isOpen={showCallbackModal} 
        onClose={() => setShowCallbackModal(false)} 
      />
    </div>
  );
};

export default LandingPage;
